import React, { useState, useEffect } from 'react';
import { Form, Empty, Input, Col, Row, Button } from 'antd';
import { merge } from 'lodash';
import { dmsConfig } from '../../../config/env';
import GooglePicker from '../../../../node_modules/react-google-picker';
import { GOOGLE_FILEPICKER } from "../../../config/common";

const { Item: FormItem } = Form;
const DOCUMENT_ORIGIN = {
    DMS: "dms",
    GOOGLE_DRIVE: "gdrive"
}
const DMS_UTILS = {
    URL_PREFIX: 'https://drive.google.com/file/d/',
    URL_SUFFIX: '/preview?embedded=true',

    callDMS: dmsId => {
        const url = dmsConfig.singleDocumentUrl + '/' + dmsId;
        return fetch(url, {
            method: 'GET',
            credentials: 'include',
            redirect: 'follow',
            mode: 'cors',
            headers: {
                'access-control-allow-origin': '*',
                'Content-Type': 'application/json'
            }
        });
    },
    getUrlParams: url => {
        if (!url) { return null; }

        const split = url.split('?');
        if (split.length !== 2) {
            return null;
        } else {
            let param = split[1];
            const secondSplit = param.split('=');

            if (secondSplit.length !== 2) {
                return null;
            } else {
                return secondSplit[1];
            }
        }
    },
    getPrimaryAttachment: json => {
        let attachment = json.primaryAttachment;
        if (attachment && attachment.split && attachment.split('|||')[1]) {
            return attachment.split('|||')[1];
        } else {
            return null;
        }
    }
}

const Component = (props) => {
    const { origin } = props;
    const url = props.url ? props.url : props.dmsURL; //Backwards compatibility with version where only dms docs were supported.
    const [documentUrl, setDocumentUrl] = useState(null);
    const hasDmsLinkChecked = props.hasDmsLinkChecked === undefined ? true : props.hasDmsLinkChecked;

    useEffect(() => {
        if (origin === DOCUMENT_ORIGIN.DMS) {
            DMS_UTILS.callDMS(DMS_UTILS.getUrlParams(url))
                .then(response => response.json())
                .then(json => {
                    setDocumentUrl(DMS_UTILS.URL_PREFIX + DMS_UTILS.getPrimaryAttachment(json) + DMS_UTILS.URL_SUFFIX);
                });
        } else {
            setDocumentUrl(url);
        }

    }, [url, origin, setDocumentUrl]);


    if (documentUrl) {
        return <>
            <iframe title={documentUrl} className="dmsDoc" src={documentUrl} width="100%" height="500px" />
            { (origin === DOCUMENT_ORIGIN.DMS && hasDmsLinkChecked) && <a href={url} target="_blank" rel="noopener noreferrer">Navigate to dms page</a>}
        </>;
    } else {
        return <Empty className="dmsDoc" description="No document provided." />;
    }
};
Component.defaultProps = {
    origin: DOCUMENT_ORIGIN.DMS
};

class EditForm extends React.Component {
    /*-----------Lifecycle methods-------------*/
    constructor(props) {
        super(props);
        this.state = {
            googleDocName: props.googleDocName,
            origin: props.origin ? props.origin : (props.dmsURL ? DOCUMENT_ORIGIN.DMS : undefined),
            hasDmsLinkChecked: props.hasDmsLinkChecked !== undefined ? props.hasDmsLinkChecked : true,
        };
    }

    componentDidMount = () => {
        this.props.form.async = this.async.bind(this);
        this.props.form.mapValuesToProps = this.mapValuesToProps.bind(this);
    };
    componentDidUpdate = () => {
        if (!this.props.form.async || !this.props.form.mapValuesToProps) {
            this.props.form.async = this.async.bind(this);
            this.props.form.mapValuesToProps = this.mapValuesToProps.bind(this);
        }
    };

    /*-----------Overridden methods-------------*/
    async = (resolve, reject) => {
        if (this.state.origin === DOCUMENT_ORIGIN.DMS) {
            let param = DMS_UTILS.getUrlParams(this.props.form.getFieldValue('dmsDoc'));

            DMS_UTILS.callDMS(param)
                .then(response => response.json())
                .then(json => {
                    DMS_UTILS.getPrimaryAttachment(json) ? resolve() : reject('No primary attachment found for this DMS-document.');
                })
                .catch(err => {
                    reject('Something went wrong, please try again later.');
                });
        } else {
            resolve();
        }
    };
    mapValuesToProps = values => {
        delete values.dmsDoc;
        delete values.googleDoc;

        values.url = this.state.url;
        values.origin = this.state.origin;
        values.hasDmsLinkChecked = this.state.hasDmsLinkChecked;


        if (values.origin === DOCUMENT_ORIGIN.GOOGLE_DRIVE) {
            values.googleDocName = this.state.googleDocName;
        }

        return values;
    };

    /*-----------Validation methods-------------*/
    validateURL = (rule, value, callback) => {
        let param = DMS_UTILS.getUrlParams(value);

        if (param !== null) {
            callback();
            return;
        } else {
            callback(`Not a valid DMS URL. Please make sure you copied the correct link.`);
        }
    };
    validateNotAllEmptyFields = (rule, value, callback) => {
        const dmsUrl = this.props.form.getFieldValue('dmsDoc');

        if ((!dmsUrl || dmsUrl.trim().length === 0) && (!this.state.googleDocName || this.state.googleDocName.trim().length === 0)) {
            callback(`Either a document via DMS or via Google drive must be provided.`);
        } else {
            callback();
            return;
        }
    }

    /*-----------Event-driven methods-------------*/
    handleGoogleDocPicked = e => {
        this.setState({

            //
            url: DMS_UTILS.URL_PREFIX + e.docs[0].id + DMS_UTILS.URL_SUFFIX,
            googleDocName: e.docs[0].name,
            origin: DOCUMENT_ORIGIN.GOOGLE_DRIVE
        });

        this.props.form.setFieldsValue({ googleDoc: e.docs[0].id });
        this.props.form.setFieldsValue({ dmsDoc: undefined });

    };

    handleDMSDocChanged = e => {
        this.setState({
            url: e.target.value,
            googleDocName: undefined,
            origin: DOCUMENT_ORIGIN.DMS
        });

        //Reset drive doc 
        //Requires a small timeout, because the Field Decorator of dmsUrl sets it's value after this method.
        setTimeout(() => {
            this.props.form.setFieldsValue({ googleDoc: undefined });
            this.props.form.validateFields();
        }, 50);

    };

    handleHasDmsLinkChanged = e => {
        this.setState({
            hasDmsLinkChecked: e.target.checked
        })

    }

    /*-----------Util methods-------------*/
    arrayValuesToMergedObject = arr => {
        let paramObject = {};

        arr.forEach(param => {
            merge(paramObject, param);
        });
        return paramObject;
    };
    getDmsValidationRules = () => {
        if (this.state.origin === DOCUMENT_ORIGIN.DMS) {
            return [
                { validator: this.validateNotAllEmptyFields },
                { validator: this.validateURL }
            ];
        }
        return [{
            validator: this.validateNotAllEmptyFields
        }];
    };


    /*-----------Render method-------------*/
    render = () => {
        const { url, dmsURL, form } = this.props;
        const { getFieldDecorator } = form;
        const { googleDocName, origin, hasDmsLinkChecked } = this.state;

        return (
            <React.Fragment>
                <Row>
                    <Col span={24}>
                        <Form.Item label="Via DMS" hasFeedback>
                            {getFieldDecorator('dmsDoc', {
                                rules: this.getDmsValidationRules(),
                                validateFirst: true,
                                initialValue: origin === DOCUMENT_ORIGIN.DMS ? (url ? url : dmsURL) : undefined
                            })(<Input name="dmsDoc" placeholder="Please enter the url on DMS" onChange={this.handleDMSDocChanged} />)}
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        {(origin === DOCUMENT_ORIGIN.DMS) && (
                        <Form.Item label="Enable DMS link?" hasFeedback>
                            {getFieldDecorator('hasDmsLinkChecked',({
                                initialValue: hasDmsLinkChecked,
                            }))(<Input name="dmsLink" type="checkbox" onClick={this.handleHasDmsLinkChanged} checked={hasDmsLinkChecked} />)}
                        </Form.Item>)}
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <FormItem label="Via Google-drive">
                            {getFieldDecorator('googleDoc', { rules: [{ validator: this.validateNotAllEmptyFields }] })(
                                <GooglePicker clientId={GOOGLE_FILEPICKER.GENERAL.CLIENT_ID}
                                    developerKey={GOOGLE_FILEPICKER.GENERAL.DEVELOPER_KEY}
                                    scope={GOOGLE_FILEPICKER.GENERAL.SCOPE}
                                    onAuthFailed={data => console.log('on auth failed:', data)}
                                    multiselect={false}
                                    navHidden={true}
                                    authImmediate={false}
                                    viewId={'FOLDERS'}
                                    createPicker={(google, oauthToken) => {
                                        const googleViewId = google.picker.ViewId.DOCS;
                                        const docsView = new google.picker.DocsView(googleViewId)
                                            .setIncludeFolders(false)
                                            .setMimeTypes("application/vnd.google-apps.spreadsheet,application/vnd.google-apps.presentation,application/vnd.google-apps.document,application/vnd.google-apps.folder,application/vnd.google-apps.file,application/pdf")
                                            .setParent(GOOGLE_FILEPICKER.DOCUMENT_PICKER.FODLER_ID)
                                            .setEnableDrives(true)
                                            .setSelectFolderEnabled(false);

                                        const picker = new window.google.picker.PickerBuilder()
                                            .addView(docsView)
                                            .setOAuthToken(oauthToken)
                                            .setDeveloperKey(GOOGLE_FILEPICKER.GENERAL.DEVELOPER_KEY)
                                            .enableFeature(google.picker.Feature.SUPPORT_DRIVES)
                                            .enableFeature(google.picker.Feature.NAV_HIDDEN)
                                            .setCallback((e) => {
                                                if (e.action === "picked") {
                                                    this.handleGoogleDocPicked(e);
                                                }
                                            });

                                        picker.build().setVisible(true);
                                    }}
                                >
                                    <Button type="dashed" icon={googleDocName ? "check-circle" : "setting"} style={{ width: '100%' }}>
                                        {googleDocName ? googleDocName : "Select from Drive"}
                                    </Button>
                                </GooglePicker>
                            )}
                        </FormItem>
                    </Col>
                </Row>
            </React.Fragment>
        );
    };
}

export { Component, EditForm };
